import type { Analytics, AnalyticsAdapter, AnalyticsEvent } from "./analytics";
import type { AnalyticsPage } from "./page-context";

export interface ConsentingAdapter extends AnalyticsAdapter {
  setConsent(granted: boolean): void;
}

/** One document's measurement lifecycle. React remounts cannot duplicate a view. */
export class AnalyticsSession implements Analytics {
  private adapter?: ConsentingAdapter;
  private page?: AnalyticsPage;
  private lastPath?: string;
  private previousLocation?: string;

  constructor(private consented: boolean) {}

  ready(adapter: ConsentingAdapter): void {
    this.adapter = adapter;
    adapter.setConsent(this.consented);
    this.flush();
  }

  setConsent(granted: boolean): void {
    if (!granted) this.lastPath = undefined;
    this.consented = granted;
    this.adapter?.setConsent(granted);
    this.flush();
  }

  visit(page: AnalyticsPage, consented = this.consented): void {
    this.page = page;
    this.setConsent(consented);
  }

  publish(event: AnalyticsEvent): void {
    if (this.consented) this.adapter?.publish(event);
  }

  private flush(): void {
    if (
      !this.consented ||
      !this.adapter ||
      !this.page ||
      this.page.path === this.lastPath
    )
      return;
    const page = this.page;
    this.adapter.publish({
      type: "page-view",
      path: page.path,
      location: page.location,
      title: page.title,
      referrer: this.previousLocation ?? page.referrer,
    });
    this.lastPath = page.path;
    this.previousLocation = page.location;
  }
}
